import React, { useEffect, useState } from 'react';
import { JobFairStatus, JobFairWithCompanyName, Sponsor } from 'app/types';
import { JobfairService } from 'app/services/JobfairService';
import { KTSVG } from '_metronic/helpers';

type SponsorViewProps = {
  subdomain: string;
};

const SponsorView: React.FC<SponsorViewProps> = ({ subdomain }) => {
  const [sponsor, setSponsor] = useState<Sponsor | null>(null);
  const [jobFairs, setJobFairs] = useState<JobFairWithCompanyName[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchSponsor = async () => {
      setIsLoading(true);
      try {
        const jobFairService = new JobfairService();
        const sponsorData = await jobFairService.getJobfairsAndSponsorBySubdomain(subdomain);
        setSponsor(sponsorData.sponsor);
        const activatedFairs = sponsorData.jobFairs;
        const fairsWithStatus = await Promise.all(
          activatedFairs.map(async (jf) => (await jobFairService.applyStatus(jf)) as JobFairWithCompanyName)
        );
        setJobFairs(fairsWithStatus);
      } catch (err) {
        console.error('Error fetching sponsor:', err);
        setError('Sponsor not found.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSponsor();
  }, [subdomain]);

  const activeJobFairs = jobFairs.filter((jf) => jf.status !== JobFairStatus.Ended);
  const pastJobFairs = jobFairs.filter((jf) => jf.status === JobFairStatus.Ended);

  const getMainAppUrl = () => {
    const { protocol, hostname, port } = window.location;
    const parts = hostname.split('.');
    if (parts.length > 2) {
      const isAmplify = hostname.includes('amplifyapp.com');
      const baseSubdomain = isAmplify ? 'main' : 'www';
      return `${protocol}//${baseSubdomain}.${parts.slice(1).join('.')}${port ? `:${port}` : ''}`;
    }
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  };

  const renderJobFairCard = (jobFair: JobFairWithCompanyName, isPast: boolean) => (
    <div key={jobFair.id} className='col-md-6 col-xl-4'>
      <div className={`card h-100 ${isPast ? 'bg-light opacity-75' : 'shadow-sm'}`} style={isPast ? { filter: 'grayscale(0.5)' } : {}}>
        <div className='card-body d-flex flex-column'>
          <div className='d-flex flex-stack mb-3'>
            <div className={`badge ${isPast ? 'badge-light-dark' : 'badge-light-primary'} fs-7 fw-bold`}>
              {new Date(jobFair.startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
            </div>
            {isPast && <div className='badge badge-light-danger fs-8 fw-bold'>Finished</div>}
          </div>

          <div className='mb-5'>
            <div className={`fs-4 fw-bold mb-2 ${isPast ? 'text-gray-600' : ''}`}>{jobFair.name}</div>
            <div className='fs-6 text-gray-500'>{jobFair.companyName}</div>
          </div>

          <div className='d-flex flex-stack mt-auto pt-5'>
            <div className='text-gray-600 fs-7 fw-semibold'>
              <KTSVG path='/media/icons/duotone/Home/Clock.svg' className='svg-icon-2 me-1' />
              {new Date(`${jobFair.startDate}T${jobFair.startTime}`).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })} - {new Date(new Date(`${jobFair.startDate}T${jobFair.startTime}`).getTime() +
                (jobFair.durationHours * 60 + jobFair.durationMinutes) * 60000)
                .toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </div>
            {!isPast && (
              <a
                href={`${getMainAppUrl()}/job-seeker/organizations/${jobFair.companyId}/jobfairs/${jobFair.id}`}
                className='btn btn-sm btn-primary'
              >
                Register Now
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-vh-100'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (!sponsor) {
    return <div>No sponsor data available.</div>;
  }

  return (
    <>
      {/* Hero Section */}
      <div
        className='bgi-no-repeat bgi-size-cover bgi-position-center position-relative mb-10'
        style={{
          backgroundImage: 'url("/media/misc/career-unsplash.jpg")',
          height: '320px'
        }}
      >
        <div className='bg-dark position-absolute w-100 h-100' style={{ opacity: '0.7' }}></div>
        <div className='container position-relative'>
          <div className='d-flex flex-column align-items-center justify-content-center min-h-300px text-center'>
            <h1 className='text-white fs-2qx fw-bolder mb-5'>Job fairs sponsored by - {sponsor?.name}</h1>
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className='container mb-10'>
        {activeJobFairs.length > 0 ? (
          <div className='mb-15'>
            <h2 className='text-dark fw-bold mb-7'>Upcoming & Active Events</h2>
            <div className='row g-6 g-xl-9'>
              {activeJobFairs.map((jobFair) => renderJobFairCard(jobFair, false))}
            </div>
          </div>
        ) : (
          !pastJobFairs.length && (
            <div className='card shadow-sm mb-15'>
              <div className='card-body pt-0'>
                <div className='py-10 text-center'>
                  <KTSVG path='/media/icons/duotone/Communication/Shield-thunder.svg' className='svg-icon-5tx text-gray-400 mb-5' />
                  <div className='fs-2x fw-bold text-gray-800 mb-2'>No Job Fairs Available</div>
                  <div className='fs-6 text-gray-600'>Check back later for upcoming opportunities</div>
                </div>
              </div>
            </div>
          )
        )}

        {pastJobFairs.length > 0 && (
          <div className='mt-20'>
            <div className='separator separator-dashed border-gray-300 mb-10'></div>
            <h2 className='text-gray-500 fw-bold mb-7'>Past Events</h2>
            <div className='row g-6 g-xl-9'>
              {pastJobFairs.map((jobFair) => renderJobFairCard(jobFair, true))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SponsorView;
